import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogContent, IconButton, LinearProgress,
  Snackbar, Typography,
} from '@mui/material';
import { Close, KeyboardVoice, PhotoCameraBack } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../shared/ConfirmDialog';
import { usePermissions } from '../../hooks/usePermissions';
import type { ObservationMedia } from '../../types/observationMedia';
import {
  getObservationMedia, requestUploadUrl, uploadToBlob, confirmUpload,
  updateCaption, removeObservationMedia,
} from '../../services/observationMediaService';
import { AudioRecorderDialog } from './AudioRecorderDialog';
import { ObservationMediaCard } from './ObservationMediaCard';

interface ObservationMediaPanelProps {
  workOrderDetailKey: string;
  /** Force read-only regardless of role (role gating is applied automatically). */
  readOnly?: boolean;
}

/**
 * Internal observation media — photo / video / audio evidence recorded by
 * technicians (typically on an iPad at the machine). Lives NEXT TO the
 * internal "Additional Observations" notes, deliberately not on the Photos
 * tab: report photos are client-facing, these attachments never leave the lab.
 */
export const ObservationMediaPanel = ({ workOrderDetailKey, readOnly = false }: ObservationMediaPanelProps) => {
  const theme = useTheme();
  const { canEditTestData } = usePermissions();
  const canEdit = canEditTestData && !readOnly;

  const [items, setItems] = useState<ObservationMedia[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [uploading, setUploading] = useState<{ name: string; pct: number } | null>(null);
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [viewer, setViewer] = useState<ObservationMedia | null>(null);
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>(
    { open: false, message: '', severity: 'success' },
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const data = await getObservationMedia(workOrderDetailKey);
      // Field-scoped evidence (FieldKey set) lives on its field's camera
      // button, not in this test-level panel.
      setItems(data.filter((m) => m.UploadStatus === 'complete' && !m.FieldKey));
    } catch { /* non-critical — panel just shows empty */ }
    setLoaded(true);
  }, [workOrderDetailKey]);

  useEffect(() => { load(); }, [load]);

  const uploadFile = async (file: File, durationSeconds?: number) => {
    setUploading({ name: file.name, pct: 0 });
    try {
      const { observationMediaKey, uploadUrl } = await requestUploadUrl(workOrderDetailKey, {
        FileName: file.name,
        FileSize: file.size,
        MimeType: file.type || 'application/octet-stream',
        DurationSeconds: durationSeconds ?? null,
      });
      await uploadToBlob(uploadUrl, file, file.type, (pct) => setUploading({ name: file.name, pct }));
      const confirmed = await confirmUpload(observationMediaKey);
      setItems((prev) => [confirmed, ...prev]);
      setSnackbar({ open: true, message: 'Attached', severity: 'success' });
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || 'Upload failed';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setUploading(null);
    }
  };

  const handleFilesPicked = async (files: FileList | null) => {
    if (!files?.length) return;
    for (const file of Array.from(files)) {
      // Sequential — keeps memory sane for large iPad videos
      // eslint-disable-next-line no-await-in-loop
      await uploadFile(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = (item: ObservationMedia) => {
    setConfirmDlg({
      open: true,
      title: 'Remove this attachment?',
      message: `"${item.Caption || item.FileName}" will no longer be visible on this test. The file itself is kept in the lab's storage archive.`,
      confirmLabel: 'Remove',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          await removeObservationMedia(item.ObservationMediaKey);
          setItems((prev) => prev.filter((m) => m.ObservationMediaKey !== item.ObservationMediaKey));
          setSnackbar({ open: true, message: 'Removed', severity: 'success' });
        } catch {
          setSnackbar({ open: true, message: 'Failed to remove', severity: 'error' });
        }
      },
    });
  };

  const handleCaptionSave = async (item: ObservationMedia, caption: string | null) => {
    try {
      const updated = await updateCaption(item.ObservationMediaKey, caption);
      setItems((prev) => prev.map((m) => (m.ObservationMediaKey === updated.ObservationMediaKey ? updated : m)));
    } catch {
      setSnackbar({ open: true, message: 'Failed to save caption', severity: 'error' });
    }
  };

  if (!loaded && items.length === 0) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
          Media Evidence
        </Typography>
        <Chip label="internal — not on the report" size="small" variant="outlined"
          sx={{ height: 18, fontSize: '0.6rem', color: 'text.secondary' }} />
        {canEdit && (
          <Box sx={{ display: 'flex', gap: 0.75, ml: 'auto' }}>
            <Button
              size="small" variant="outlined" startIcon={<PhotoCameraBack sx={{ fontSize: 16 }} />}
              onClick={() => fileInputRef.current?.click()}
              disabled={!!uploading}
              sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25, minHeight: 34 }}
            >
              Photo / Video
            </Button>
            <Button
              size="small" variant="outlined" startIcon={<KeyboardVoice sx={{ fontSize: 16 }} />}
              onClick={() => setRecorderOpen(true)}
              disabled={!!uploading}
              sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25, minHeight: 34 }}
            >
              Record Audio
            </Button>
          </Box>
        )}
      </Box>

      {/* On iPad/iPhone the picker offers "Take Photo or Video" natively */}
      <input
        ref={fileInputRef} type="file" hidden multiple
        accept="image/*,video/*,audio/*"
        onChange={(e) => handleFilesPicked(e.target.files)}
      />

      {uploading && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
            Uploading {uploading.name}… {uploading.pct}%
          </Typography>
          <LinearProgress variant="determinate" value={uploading.pct} sx={{ height: 4, borderRadius: 2 }} />
        </Box>
      )}

      {items.length === 0 ? (
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
          No media attached{canEdit ? ' — take a photo, video, or voice note for the reviewer.' : '.'}
        </Typography>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: 1,
        }}>
          {items.map((item) => (
            <ObservationMediaCard
              key={item.ObservationMediaKey}
              item={item}
              canEdit={canEdit}
              onOpen={setViewer}
              onDelete={handleDelete}
              onCaptionSave={handleCaptionSave}
            />
          ))}
        </Box>
      )}

      <AudioRecorderDialog
        open={recorderOpen}
        onClose={() => setRecorderOpen(false)}
        onAttach={(file, durationSeconds) => uploadFile(file, durationSeconds)}
      />

      {/* Full-size viewer for photos and videos */}
      <Dialog open={!!viewer} onClose={() => setViewer(null)} maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 2, backgroundColor: alpha(theme.palette.common.black, 0.92) } }}>
        <IconButton onClick={() => setViewer(null)}
          sx={{ position: 'absolute', top: 8, right: 8, color: theme.palette.common.white, zIndex: 1 }}>
          <Close />
        </IconButton>
        <DialogContent sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {viewer?.MediaType === 'photo' && (
            <Box component="img" src={viewer.mediaUrl} alt={viewer.FileName}
              sx={{ maxWidth: '86vw', maxHeight: '82vh', objectFit: 'contain' }} />
          )}
          {viewer?.MediaType === 'video' && (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <Box component="video" src={viewer.mediaUrl} controls autoPlay playsInline
              sx={{ maxWidth: '86vw', maxHeight: '82vh' }} />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'success' ? 2000 : 4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
